import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import LogoSpin from '../../../shared/animations/LogoSpin';
import { useTheme } from '../../../styles/ThemeContext';
import './DivisionSwitchLoader.css';

const STEPS = [
  { id: 1, label: 'Verified' },
  { id: 2, label: 'Switching' },
  { id: 3, label: 'Refreshing' },
  { id: 4, label: 'Ready' },
];

// Compact card + dimmed/blurred app behind — same footprint as the DivisionSwitcher's
// current truck/road overlay, but built from the PostLoginLoader "family" (glow, LogoSpin
// ring, gradient headline, step dots) so the two post-auth loading moments read as siblings
// without being identical. See .claude/wireframes/division-switch-loader-family-wireframes.html
// (Concept B) for the approved direction.
const hexToRgb = (hex) => {
  const clean = hex?.replace('#', '') || '9404C3';
  const bigint = parseInt(clean.length === 3
    ? clean.split('').map((c) => c + c).join('')
    : clean, 16);
  return `${(bigint >> 16) & 255}, ${(bigint >> 8) & 255}, ${bigint & 255}`;
};

const DivisionSwitchLoader = ({ divisionName = 'division', activeStep = 2 }) => {
  const { isDarkMode, theme } = useTheme();

  // Drives every purple-ish value in DivisionSwitchLoader.css off the active org color
  // theme (purple/green/blue/red/yellow from Themes.css) instead of a hardcoded hex,
  // so this loader follows theme switching the same way the rest of the app does.
  const accent = theme?.primary || '#9404C3';
  const accentLight = theme?.accentHighlight || accent;
  const accentDim = theme?.secondary || accent;
  const accentRgb = hexToRgb(accent);
  const accentVars = {
    '--dsl-accent': accent,
    '--dsl-accent-rgb': accentRgb,
    '--dsl-accent-light': accentLight,
    '--dsl-accent-dim': accentDim,
    '--dsl-accent-tint': accent,
  };

  return createPortal(
    <div className={`dsl-overlay ${isDarkMode ? 'dsl-dark' : 'dsl-light'}`} style={accentVars}>
      <div className="dsl-glow" />
      <div className="dsl-card">
        <div className="dsl-logo-wrap">
          <div className="dsl-logo-ring" />
          {/* LogoSpin's neumorphic circle is hardcoded near-black for the always-dark
              login/OAuth screens it was built for — in a light card it reads as a flat
              black hole, so give it a light neumorphic backing plate here instead of
              editing the shared component. */}
          <div className="dsl-logo-plate">
            <LogoSpin size={88} />
          </div>
        </div>

        <div className="dsl-headline">
          <h3>Switching to <span className="dsl-accent">{divisionName}</span></h3>
          <p className="dsl-sub">This will only take a moment</p>
        </div>

        <div className="dsl-track">
          {STEPS.map((step, i) => {
            const isDone = step.id < activeStep;
            const isActive = step.id === activeStep;
            const stepClass = isDone ? 'dsl-step done' : isActive ? 'dsl-step active' : 'dsl-step';
            const lineClass = step.id < activeStep ? 'dsl-line lit' : 'dsl-line';
            return (
              <div key={step.id} className="dsl-step-wrap">
                <div className={stepClass}>
                  <div className="dsl-dot" />
                  <span className="dsl-step-label">{step.label}</span>
                </div>
                {i < STEPS.length - 1 && <div className={lineClass} />}
              </div>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
};

DivisionSwitchLoader.propTypes = {
  divisionName: PropTypes.string,
  activeStep: PropTypes.number,
};

export default DivisionSwitchLoader;
